import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon, PrimaryButton, GhostButton } from "@/components/kit";
import { api, fileUrl, fileUrlAuth } from "@/api";
import { useI18n } from "@/i18n";

// Accepte le point ET la virgule pour les quantités/métrés
const num = (v) => Number["parseFloat"](String(v ?? "").replace(/,/g, "."));
const eur = (n) => Math.round(n).toLocaleString("fr-FR");

const L = {
  fr: { previewTitle: "Aperçu du devis", pose: "Coût de pose estimé", poseFirm: "Prix de pose", min: "Min (€)", max: "Max (€)",
    supplies: "Fournitures (postes)", add: "Ajouter un poste", newItem: "Nouveau poste",
    qty: "Qté", price: "P.U. €", line: "Total", supplyTotal: "Total fournitures", poseAcc: "Accessoires de pose (inclus dans la pose)", addPose: "Ajouter un accessoire de pose", poseTotalEst: "Total pose estimée (+ accessoires)", poseTotalFirm: "Total pose (accessoires inclus)", attest: "Attestation TVA 6 %", htva: "Total HTVA", vat: "TVA", ttc: "Total TTC",
    saved: "Aperçu recalculé ✓", del: "Supprimer le devis",
    recalc: "Recalculer l'aperçu", finalizeHint: "Appuyez sur « Recalculer l'aperçu » pour mettre à jour TVA/TTC avant de télécharger.",
    recalcNote: "Recalculez pour mettre à jour la TVA et le TTC.", dirty: "Modifié — à recalculer",
    delConfirm: "Supprimer définitivement ce devis ?", empty: "Aucun poste. Ajoutez-en un.",
    resend: "Renvoyer le devis modifié au client", resending: "Envoi en cours…",
    resent: "Devis renvoyé au client", resendFail: "Échec de l'envoi au client (voir email)",
    resend_owner: "Renvoyer le Word modifiable au gestionnaire (Déco Ligot / pro)", resent_owner: "Word envoyé au gestionnaire",
    resendOwnerFail: "Échec de l'envoi au gestionnaire (voir email)",
    docType: "Type de document", estimation: "Estimation marché", devis: "Devis (engageant)",
    devisHint: "En mode Devis : titre « Devis », suppression des mentions « sans engagement », ajout du « Bon pour accord » et de la clause d'acompte avec votre IBAN.",
    deposit: "Acompte (%)", poseSingle: "Prix de pose (€)", ibanMissing: "⚠️ Aucun IBAN configuré — ajoutez-le dans Réglages société pour qu'il apparaisse sur le devis.",
    cat: { curtains: "Tentures & voilages", flooring: "Sols & parquets", wallpaper: "Papier peint", painting: "Peinture" } },
  nl: { previewTitle: "Offerte-preview", pose: "Geschatte plaatsingskost", poseFirm: "Plaatsingsprijs", min: "Min (€)", max: "Max (€)",
    supplies: "Materialen (posten)", add: "Post toevoegen", newItem: "Nieuwe post",
    qty: "Aant.", price: "P/st €", line: "Totaal", supplyTotal: "Totaal materialen", poseAcc: "Plaatsingstoebehoren (in plaatsing inbegrepen)", addPose: "Plaatsingstoebehoren toevoegen", poseTotalEst: "Totaal geschatte plaatsing (+ toebehoren)", poseTotalFirm: "Totaal plaatsing (toebehoren inbegrepen)", attest: "Btw-attest 6 %", htva: "Totaal excl. btw", vat: "Btw", ttc: "Totaal incl. btw",
    saved: "Preview herberekend ✓", del: "Offerte verwijderen",
    recalc: "Preview herberekenen", finalizeHint: "Druk op 'Preview herberekenen' om btw/incl. bij te werken voor u downloadt.",
    recalcNote: "Herbereken om btw en incl. btw bij te werken.", dirty: "Gewijzigd — herberekenen",
    delConfirm: "Deze offerte definitief verwijderen?", empty: "Geen posten. Voeg er een toe.",
    resend: "Gewijzigde offerte naar klant sturen", resending: "Versturen…",
    resent: "Offerte naar klant verzonden", resendFail: "Verzenden naar klant mislukt (zie e-mail)",
    resend_owner: "Bewerkbaar Word naar beheerder sturen (Déco Ligot / pro)", resent_owner: "Word naar beheerder verzonden",
    resendOwnerFail: "Verzenden naar beheerder mislukt (zie e-mail)",
    docType: "Type document", estimation: "Marktraming", devis: "Offerte (bindend)",
    devisHint: "In Offerte-modus: titel « Offerte », zonder « zonder verbintenis », met « Goedgekeurd » en de voorschotclausule met uw IBAN.",
    deposit: "Voorschot (%)", poseSingle: "Plaatsingsprijs (€)", ibanMissing: "⚠️ Geen IBAN ingesteld — voeg het toe in Bedrijfsinstellingen.",
    cat: { curtains: "Gordijnen & vitrages", flooring: "Vloeren & parket", wallpaper: "Behang", painting: "Schilderwerk" } },
  en: { previewTitle: "Quote preview", pose: "Estimated labour", poseFirm: "Labour price", min: "Min (€)", max: "Max (€)",
    supplies: "Supplies (line items)", add: "Add line item", newItem: "New item",
    qty: "Qty", price: "Unit €", line: "Total", supplyTotal: "Supplies total", poseAcc: "Installation accessories (included in labour)", addPose: "Add installation accessory", poseTotalEst: "Estimated installation total (+ accessories)", poseTotalFirm: "Installation total (accessories included)", attest: "VAT 6% certificate", htva: "Total excl. VAT", vat: "VAT", ttc: "Total incl. VAT",
    saved: "Preview recalculated ✓", del: "Delete quote",
    recalc: "Recalculate preview", finalizeHint: "Tap 'Recalculate preview' to update VAT/incl. before downloading.",
    recalcNote: "Recalculate to update VAT and incl. VAT.", dirty: "Edited — recalculate",
    delConfirm: "Permanently delete this quote?", empty: "No line items. Add one.",
    resend: "Resend the edited quote to client", resending: "Sending…",
    resent: "Quote resent to client", resendFail: "Sending to client failed (check email)",
    resend_owner: "Send the editable Word to the manager (Déco Ligot / pro)", resent_owner: "Word sent to the manager",
    resendOwnerFail: "Sending to manager failed (check email)",
    docType: "Document type", estimation: "Market estimate", devis: "Quote (binding)",
    devisHint: "In Quote mode: title « Quote », removes the non-binding notes, adds « Approved » and the deposit clause with your IBAN.",
    deposit: "Deposit (%)", poseSingle: "Labour price (€)", ibanMissing: "⚠️ No IBAN set — add it in Company settings so it appears on the quote.",
    cat: { curtains: "Curtains & sheers", flooring: "Floors & parquet", wallpaper: "Wallpaper", painting: "Painting" } },
  de: { previewTitle: "Angebots-Vorschau", pose: "Geschätzte Verlegekosten", poseFirm: "Verlegepreis", min: "Min (€)", max: "Max (€)",
    supplies: "Material (Posten)", add: "Posten hinzufügen", newItem: "Neuer Posten",
    qty: "Menge", price: "Stk €", line: "Summe", supplyTotal: "Summe Material", poseAcc: "Verlegezubehör (in Verlegung enthalten)", addPose: "Verlegezubehör hinzufügen", poseTotalEst: "Geschätzte Verlegung gesamt (+ Zubehör)", poseTotalFirm: "Verlegung gesamt (Zubehör inbegriffen)", attest: "MwSt.-Bescheinigung 6 %", htva: "Summe netto", vat: "MwSt.", ttc: "Summe brutto",
    saved: "Vorschau neu berechnet ✓", del: "Angebot löschen",
    recalc: "Vorschau neu berechnen", finalizeHint: "Auf 'Vorschau neu berechnen' klicken, um MwSt./brutto vor dem Download zu aktualisieren.",
    recalcNote: "Neu berechnen, um MwSt. und brutto zu aktualisieren.", dirty: "Geändert — neu berechnen",
    delConfirm: "Dieses Angebot endgültig löschen?", empty: "Keine Posten. Fügen Sie einen hinzu.",
    resend: "Geändertes Angebot an Kunden senden", resending: "Wird gesendet…",
    resent: "Angebot an Kunden gesendet", resendFail: "Senden an Kunden fehlgeschlagen (E-Mail prüfen)",
    resend_owner: "Bearbeitbares Word an den Verwalter senden (Déco Ligot / Pro)", resent_owner: "Word an Verwalter gesendet",
    resendOwnerFail: "Senden an Verwalter fehlgeschlagen (E-Mail prüfen)",
    docType: "Dokumenttyp", estimation: "Marktschätzung", devis: "Angebot (verbindlich)",
    devisHint: "Im Angebotsmodus: Titel « Angebot », ohne unverbindliche Hinweise, mit « Einverstanden » und der Anzahlungsklausel mit Ihrer IBAN.",
    deposit: "Anzahlung (%)", poseSingle: "Verlegepreis (€)", ibanMissing: "⚠️ Keine IBAN hinterlegt — in den Firmeneinstellungen hinzufügen.",
    cat: { curtains: "Vorhänge & Stores", flooring: "Böden & Parkett", wallpaper: "Tapeten", painting: "Anstrich" } },
};

// Étapes standard de tapissage (papier peint) — libellés localisés, éditables/ajout/suppression
const WP_STEPS = {
  fr: ["Préparation des supports (dépose, dépoussiérage)", "Réparation et rebouchage des fissures", "Lissage / enduit de lissage", "Sous-couche / primaire d'accrochage", "Encollage et pose du papier peint", "Découpes et raccords", "Finitions et nettoyage de chantier"],
  nl: ["Voorbereiding ondergrond (verwijderen, ontstoffen)", "Herstel en dichten van scheuren", "Uitvlakken / plamuren", "Grondlaag / hechtprimer", "Lijmen en plaatsen van behang", "Snijwerk en naadaansluitingen", "Afwerking en opkuis werf"],
  en: ["Surface preparation (stripping, dusting)", "Repair and crack filling", "Skim coat / smoothing", "Undercoat / bonding primer", "Pasting and hanging the wallpaper", "Cutting and pattern matching", "Finishing and site cleaning"],
  de: ["Untergrundvorbereitung (Entfernen, Entstauben)", "Reparatur und Rissverspachtelung", "Glättung / Spachtelung", "Grundierung / Haftgrund", "Kleben und Tapezieren", "Zuschnitt und Musteransatz", "Endbearbeitung und Baustellenreinigung"],
};
const WP_UNIT = { fr: "forfait", nl: "forfait", en: "flat rate", de: "Pauschale" };
const WP_SPLIT = [0.12, 0.08, 0.12, 0.08, 0.42, 0.08, 0.10];

function buildWallpaperSteps(q) {
  const lang = q.lang || "fr";
  const labels = WP_STEPS[lang] || WP_STEPS.fr;
  const unit = WP_UNIT[lang] || WP_UNIT.fr;
  const bdSum = Array.isArray(q.estimate_breakdown) ? q.estimate_breakdown.reduce((s, b) => s + (b.amount || 0), 0) : 0;
  const total = q.estimate_min != null ? q.estimate_min : bdSum;
  const rows = labels.map((label, i) => ({
    key: "custom", label, qty: "1", unit, unit_price: String(Math.round(total * WP_SPLIT[i])), group: "pose",
  }));
  const sum = rows.reduce((s, r) => s + (num(r.unit_price) || 0), 0);
  const delta = Math.round((total - sum) * 100) / 100;
  if (delta !== 0) rows[4].unit_price = String(Math.round(((num(rows[4].unit_price) || 0) + delta) * 100) / 100);
  return rows;
}

// Reprend FIDÈLEMENT le détail réel de l'estimation (libellés d'origine) en postes de pose éditables,
// mis à l'échelle sur estimate_min (total de pose faisant foi) pour conserver le prix.
function seedPoseFromBreakdown(q, bd) {
  const seeded = bd.map((b) => ({
    key: "custom", label: b.label || "", qty: "1", unit: "",
    unit_price: String(Math.round((b.amount || 0) * 100) / 100), group: "pose",
  }));
  const sum = bd.reduce((s, b) => s + (b.amount || 0), 0);
  const target = q.estimate_min != null ? q.estimate_min : sum;
  const delta = Math.round((target - sum) * 100) / 100;
  if (delta !== 0 && seeded.length) {
    let mi = 0, mv = -Infinity;
    bd.forEach((b, i) => { if ((b.amount || 0) > mv) { mv = b.amount || 0; mi = i; } });
    seeded[mi].unit_price = String(Math.round(((num(seeded[mi].unit_price) || 0) + delta) * 100) / 100);
  }
  return seeded;
}


export default function QuoteEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const tt = L[lang] || L.fr;

  const [items, setItems] = useState([]);
  const [poseMin, setPoseMin] = useState("");
  const [poseMax, setPoseMax] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [meta, setMeta] = useState({});
  const [tot, setTot] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  const [resendingOwner, setResendingOwner] = useState(false);
  const [resendOwnerMsg, setResendOwnerMsg] = useState(null);
  const [docType, setDocType] = useState("estimation");
  const [depositPct, setDepositPct] = useState("50");

  const markDirty = () => { setDirty(true); setSaved(false); };

  const applyQuote = (q) => {
    const rawItems = (q.supply_items || []).map((it) => ({
      key: it.key || "", label: it.label || "", qty: String(it.qty ?? ""),
      unit: it.unit || "", unit_price: String(it.unit_price ?? ""), group: it.group || "supply",
    }));
    const bd = Array.isArray(q.estimate_breakdown) ? q.estimate_breakdown : [];
    const isPaperOrPaint = q.category === "wallpaper" || q.category === "painting";
    if (isPaperOrPaint) {
      // Papier peint / peinture : on reprend FIDÈLEMENT tous les détails de l'estimation d'origine —
      //  • fournitures (avec leurs références dans le libellé) conservées telles quelles
      //  • main d'oeuvre = détail réel du devis (libellés d'origine), éditable ligne par ligne
      const fournitures = rawItems.filter((it) => it.group !== "pose");
      const existingPose = rawItems.filter((it) => it.group === "pose");
      let poseItems;
      if (existingPose.length > 0) poseItems = existingPose;          // déjà modifié -> détail édité repris tel quel
      else if (bd.length > 0) poseItems = seedPoseFromBreakdown(q, bd); // reprise fidèle du détail réel
      else if (q.category === "wallpaper") poseItems = buildWallpaperSteps(q);
      else poseItems = [];
      setItems([...fournitures, ...poseItems]);
      setPoseMin("0"); setPoseMax("0");
    } else {
      setItems(rawItems);
      setPoseMin(q.estimate_min != null ? String(q.estimate_min) : "");
      setPoseMax(q.estimate_max != null ? String(q.estimate_max) : "");
    }
    setDocType(q.doc_type || "estimation");
    setDepositPct(q.deposit_pct != null ? String(q.deposit_pct) : "50");
    setMeta({ client_name: q.client_name, client_email: q.client_email, category: q.category, edited: !!q.edited, over10y: q.building_over_10y === true });
    setTot({ supply_cost: q.supply_cost, total_min: q.total_min, total_max: q.total_max, vat_min: q.vat_min, vat_max: q.vat_max, ttc_min: q.ttc_min, ttc_max: q.ttc_max, vat_note: q.vat_note });
  };

  useEffect(() => {
    setLoading(true);
    api.getQuote(id).then(applyQuote).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id]);

  const setItem = (idx, field, value) => { markDirty(); setItems((s) => s.map((it, i) => (i === idx ? { ...it, [field]: value } : it))); };
  const removeItem = (idx) => { markDirty(); setItems((s) => s.filter((_, i) => i !== idx)); };
  const addItem = () => { markDirty(); setItems((s) => [...s, { key: "custom", label: tt.newItem, qty: "1", unit: "", unit_price: "0", group: "supply" }]); };
  const addPoseItem = () => { markDirty(); setItems((s) => [...s, { key: "custom", label: tt.newItem, qty: "1", unit: "", unit_price: "0", group: "pose" }]); };

  const lineTotal = (it) => (num(it.qty) || 0) * (num(it.unit_price) || 0);
  const devis = docType === "devis";
  const estimateMode = !devis && !dirty && !meta.edited;
  const POSE_GROUPS = ["floor", "pose"];
  const isPose = (it) => it.key !== "floor_covering" && POSE_GROUPS.includes(it.group);
  const rows = items.map((it, idx) => ({ it, idx }));
  const supplyRows = rows.filter((r) => !isPose(r.it));
  const poseRows = rows.filter((r) => isPose(r.it));
  const supplyTotal = supplyRows.reduce((sum, r) => sum + lineTotal(r.it), 0);
  const poseAccTotal = poseRows.reduce((sum, r) => sum + lineTotal(r.it), 0);
  const pMin = num(poseMin) || 0;
  const pMax = num(poseMax) || 0;
  const poseBaseMin = devis ? pMax : Math.min(pMin, pMax || pMin);
  const poseBaseMax = devis ? pMax : Math.max(pMin, pMax);
  const htMin = poseBaseMin + poseAccTotal + supplyTotal;
  const htMax = poseBaseMax + poseAccTotal + supplyTotal;
  const rng = (a, b) => (devis || Math.round(a || 0) === Math.round(b || 0)) ? `${eur(a)} €` : `${eur(a)} – ${eur(b)} €`;

  const renderItem = (r) => {
    const { it, idx } = r;
    return (
      <div key={idx} data-testid={`edit-item-${idx}`} className="mb-3 border-t border-divider pt-3 first:border-0 first:pt-0">
        <div className="mb-2 flex items-start gap-2">
          <input data-testid={`edit-item-label-${idx}`} value={it.label} onChange={(e) => setItem(idx, "label", e.target.value)} placeholder={tt.newItem} className="field-input flex-1 py-2 text-[14px]" />
          <button data-testid={`edit-item-del-${idx}`} onClick={() => removeItem(idx)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line">
            <Icon name="x" size={16} className="text-err" />
          </button>
        </div>
        <div className="grid grid-cols-3 items-end gap-2">
          <label>
            <span className="mb-1 block text-[12px] text-mute">{tt.qty} {it.unit ? `(${it.unit})` : ""}</span>
            <input data-testid={`edit-item-qty-${idx}`} value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} inputMode="decimal" className="field-input py-2" />
          </label>
          <label>
            <span className="mb-1 block text-[12px] text-mute">{tt.price}</span>
            <input data-testid={`edit-item-price-${idx}`} value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} inputMode="decimal" className="field-input py-2" />
          </label>
          <div>
            <span className="mb-1 block text-[12px] text-mute">{tt.line}</span>
            <p className="py-2 text-[15px] font-semibold text-ink">{Math.round(lineTotal(it))} €</p>
          </div>
        </div>
      </div>
    );
  };

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const updated = await api.editQuote(id, {
        supply_items: items.map((it) => ({ key: it.key, label: it.label, qty: num(it.qty) || 0, unit: it.unit, unit_price: num(it.unit_price) || 0, group: it.group })),
        estimate_min: devis ? (poseMax === "" ? null : num(poseMax)) : (poseMin === "" ? null : num(poseMin)),
        estimate_max: poseMax === "" ? null : num(poseMax),
        doc_type: docType,
        deposit_pct: depositPct === "" ? 50 : num(depositPct),
      });
      applyQuote(updated); setSaved(true); setDirty(false);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(tt.delConfirm)) return;
    try { await api.deleteQuote(id); navigate("/admin/dashboard", { replace: true }); }
    catch (e) { setError(e.message); }
  };

  const doResend = async () => {
    setResending(true); setResendMsg(null);
    try {
      const r = await api.resendQuote(id);
      setResendMsg({ ok: r.client_sent, text: r.client_sent ? `${tt.resent} (${r.client_email})` : tt.resendFail });
    } catch (e) {
      setResendMsg({ ok: false, text: e.message || tt.resendFail });
    } finally {
      setResending(false);
    }
  };

  const doResendOwner = async () => {
    setResendingOwner(true); setResendOwnerMsg(null);
    try {
      const r = await api.resendWordOwner(id);
      setResendOwnerMsg({ ok: r.sent, text: r.sent ? `${tt.resent_owner} (${r.owner_email})` : tt.resendOwnerFail });
    } catch (e) {
      setResendOwnerMsg({ ok: false, text: e.message || tt.resendOwnerFail });
    } finally {
      setResendingOwner(false);
    }
  };

  const showHtOnly = dirty || !tot || tot.ttc_min == null;

  return (
    <div className="mx-auto max-w-3xl px-5 py-8 pb-[340px]">
      <div className="mb-5 flex items-center gap-3">
        <button data-testid="edit-back" onClick={() => navigate("/admin/dashboard")} className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white">
          <Icon name="arrow-left" size={20} className="text-ink" />
        </button>
        <h1 className="flex-1 font-display text-3xl font-semibold text-ink">{tt.previewTitle}</h1>
        <button data-testid="edit-delete" onClick={doDelete} className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white">
          <Icon name="trash-2" size={18} className="text-err" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Icon name="loader" size={32} className="animate-spin text-brand" /></div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-2xl bg-brand-tertiary p-5">
            <p className="font-display text-xl font-semibold text-brand">{tt.previewTitle}</p>
            {meta.client_name ? <p className="text-[14px] font-medium text-brand-on">{meta.client_name}{meta.client_email ? ` · ${meta.client_email}` : ""}</p> : null}
            {meta.category ? <p className="text-[13px] text-brand-on">{tt.cat[meta.category] || meta.category}</p> : null}
            {dirty ? (
              <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-err bg-paper px-2.5 py-1 text-[12px] font-medium text-err">
                <Icon name="alert-circle" size={13} /> {tt.dirty}
              </span>
            ) : null}
          </div>

          {/* Type de document : Estimation / Devis */}
          <div className="rounded-2xl border border-line bg-white p-5">
            <h3 className="mb-3 text-[15px] font-semibold text-brand">{tt.docType}</h3>
            <div className="flex gap-2">
              <button data-testid="edit-doctype-estimation" onClick={() => { setDocType("estimation"); markDirty(); }}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-[14px] font-semibold transition-colors ${docType === "estimation" ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-3"}`}>
                {tt.estimation}
              </button>
              <button data-testid="edit-doctype-devis" onClick={() => { setDocType("devis"); if (!poseMax && poseMin) setPoseMax(poseMin); markDirty(); }}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-[14px] font-semibold transition-colors ${docType === "devis" ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-3"}`}>
                {tt.devis}
              </button>
            </div>
            {docType === "devis" ? (
              <div className="mt-3">
                <p className="mb-3 text-[12px] text-mute">{tt.devisHint}</p>
                <label className="block max-w-[160px]">
                  <span className="mb-1 block text-[12px] text-mute">{tt.deposit}</span>
                  <input data-testid="edit-deposit-pct" value={depositPct} onChange={(e) => { setDepositPct(e.target.value); markDirty(); }} inputMode="decimal" className="field-input" />
                </label>
              </div>
            ) : null}
          </div>

          {/* Pose */}
          <div className="rounded-2xl border border-line bg-white p-5">
            <h3 className="mb-3 text-[15px] font-semibold text-brand">{devis ? tt.poseFirm : tt.pose}</h3>
            {devis ? (
              <label className="block max-w-[220px]">
                <span className="mb-1 block text-[12px] text-mute">{tt.poseSingle}</span>
                <input data-testid="edit-pose-single" value={poseMax} onChange={(e) => { setPoseMax(e.target.value); markDirty(); }} inputMode="decimal" className="field-input" />
              </label>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-[12px] text-mute">{tt.min}</span>
                  <input data-testid="edit-pose-min" value={poseMin} onChange={(e) => { setPoseMin(e.target.value); markDirty(); }} inputMode="decimal" className="field-input" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[12px] text-mute">{tt.max}</span>
                  <input data-testid="edit-pose-max" value={poseMax} onChange={(e) => { setPoseMax(e.target.value); markDirty(); }} inputMode="decimal" className="field-input" />
                </label>
              </div>
            )}
          </div>

          {/* Supplies */}
          <div className="rounded-2xl border border-line bg-white p-5">
            <h3 className="mb-3 text-[15px] font-semibold text-brand">{tt.supplies}</h3>
            {items.length === 0 ? <p className="text-[13px] text-mute">{tt.empty}</p> : null}
            {supplyRows.map(renderItem)}
            <button data-testid="edit-add-item" onClick={addItem} className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-brand py-3 text-[14px] font-semibold text-brand">
              <Icon name="plus" size={16} /> {tt.add}
            </button>
          </div>

          {/* Accessoires de pose (regroupés avec la pose) */}
          <div className="rounded-2xl border border-line bg-white p-5">
            <h3 className="mb-3 text-[15px] font-semibold text-brand">{tt.poseAcc}</h3>
            {poseRows.map(renderItem)}
            <button data-testid="edit-add-pose" onClick={addPoseItem} className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-brand py-3 text-[14px] font-semibold text-brand">
              <Icon name="plus" size={16} /> {tt.addPose}
            </button>
          </div>

          {/* Totals */}
          <div className="space-y-2 rounded-2xl bg-brand-tertiary p-5">
            <Line label={tt.supplyTotal} value={`${eur(supplyTotal)} €`} />
            {poseRows.length > 0 ? <Line label={estimateMode ? tt.poseTotalEst : tt.poseTotalFirm} value={rng(poseBaseMin + poseAccTotal, poseBaseMax + poseAccTotal)} /> : null}
            {showHtOnly ? (
              <>
                <Line big label={tt.htva} value={rng(htMin, htMax)} />
                <p className="text-[12px] text-err">{tt.recalcNote}</p>
              </>
            ) : (
              <>
                {tot.vat_note ? <p className="text-[11px] text-brand-on">{tot.vat_note}</p> : null}
                <Line label={tt.htva} value={rng(tot.total_min, tot.total_max)} />
                <Line label={tt.vat} value={rng(tot.vat_min, tot.vat_max)} />
                <Line big label={tt.ttc} value={rng(tot.ttc_min, tot.ttc_max)} />
              </>
            )}
          </div>

          {error ? <p data-testid="edit-error" className="text-[14px] font-medium text-err">{error}</p> : null}
          {saved ? <p data-testid="edit-saved" className="text-[14px] font-medium text-ok">{tt.saved}</p> : null}
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 border-t border-divider bg-white/95 backdrop-blur-md lg:left-64">
        <div className="mx-auto max-w-3xl px-5 py-3">
          <PrimaryButton testId="edit-save" title={tt.recalc} icon="refresh-cw" onClick={save} loading={saving} />
          {dirty ? (
            <p className="mt-2 text-center text-[12px] text-mute">{tt.finalizeHint}</p>
          ) : (
            <>
              <div className="mt-2 flex gap-3">
                <GhostButton testId="finalize-pdf" title="PDF" icon="file-text" onClick={() => window.open(fileUrlAuth(`/quotes/${id}/pdf`), "_blank")} />
                <GhostButton testId="finalize-word" title="Word" icon="file" onClick={() => window.open(fileUrlAuth(`/quotes/${id}/docx`), "_blank")} />
              </div>
              {meta.over10y ? (
                <button data-testid="finalize-attestation" onClick={() => window.open(fileUrl(`/quotes/${id}/attestation`), "_blank")}
                  className="mt-2 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border-[1.5px] border-brand bg-white px-4 text-[14px] font-semibold text-brand transition-colors hover:bg-brand-tertiary">
                  <Icon name="file-check" size={16} className="text-brand" /> {tt.attest}
                </button>
              ) : null}
              <button
                data-testid="resend-quote"
                onClick={doResend}
                disabled={resending}
                className="mt-2 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border-[1.5px] border-brand bg-brand-tertiary px-4 text-[15px] font-semibold text-brand-on transition-colors hover:brightness-95 disabled:opacity-60"
              >
                <Icon name={resending ? "loader" : "send"} size={17} className={resending ? "animate-spin text-brand" : "text-brand"} />
                {resending ? tt.resending : tt.resend}
              </button>
              {resendMsg ? (
                <p data-testid="resend-msg" className={`mt-2 text-center text-[13px] font-medium ${resendMsg.ok ? "text-ok" : "text-err"}`}>{resendMsg.text}</p>
              ) : null}
              <button
                data-testid="resend-word-owner"
                onClick={doResendOwner}
                disabled={resendingOwner}
                className="mt-2 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border-[1.5px] border-line bg-white px-4 text-[15px] font-semibold text-ink transition-colors hover:bg-paper-3 disabled:opacity-60"
              >
                <Icon name={resendingOwner ? "loader" : "file"} size={17} className={resendingOwner ? "animate-spin text-brand" : "text-brand"} />
                {resendingOwner ? tt.resending : tt.resend_owner}
              </button>
              {resendOwnerMsg ? (
                <p data-testid="resend-owner-msg" className={`mt-2 text-center text-[13px] font-medium ${resendOwnerMsg.ok ? "text-ok" : "text-err"}`}>{resendOwnerMsg.text}</p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Line = ({ label, value, big }) => (
  <div className="flex items-center justify-between">
    <span className={big ? "text-[16px] font-semibold text-brand-on" : "text-[14px] text-brand-on"}>{label}</span>
    <span className={big ? "font-display text-lg font-bold text-brand" : "text-[15px] font-semibold text-brand-on"}>{value}</span>
  </div>
);
